import type { DropdownFilterCallback } from '../../../components/Dropdown/Dropdown';
import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { UseAppForm } from '../../form';
import type { FieldPermissionsRule } from '../FieldPermissions/FieldPermisssions';
import type { PermissionContentTypeDefinition } from '../PermissionContentType/PermissionContentType';

import { EMPTY_ARRAY } from '../../../lib/globals';
import { deepReadKeyValue, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { DeleteIcon } from '../../../images/shapes';

import { useFieldContext, useFormContext } from '../../form';
import GlobalRuleCheckbox from './GlobalRuleCheckbox/GlobalRuleCheckbox';

export interface PermissionFieldProps {
  /**
   * On permission remove callback
   */
  onRemove?: () => void;
  /**
   * Permission available rules
   */
  ruleColumn?: FieldPermissionsRule[];
  /**
   * Content type options
   */
  ctdsOptions?: DropdownOption[];
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Permission type
   */
  type?: '' | 'CTD' | 'CO';
  /**
   * Content types dropdown placeholder
   */
  ctdPlaceholder?: string;
  /**
   * Callback to filter content types
   */
  ctdsFilterCallback?: DropdownFilterCallback;
  /**
   * Test id for field
   */
  testId?: string;
}

const PermissionField = ({
  onRemove,
  ruleColumn = EMPTY_ARRAY,
  ctdsOptions = EMPTY_ARRAY,
  disabled = false,
  type = '',
  ctdPlaceholder = '',
  ctdsFilterCallback,
  testId = '',
}: PermissionFieldProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext();

  return (
    <>
      <div className="w-full flex flex-row items-center">
        <form.AppField name={`${field.name}.contentTypeDefinition`}>
          {(subField) => (
            <subField.PermissionContentType
              ctdsOptions={ctdsOptions}
              placeholder={ctdPlaceholder}
              ruleColumn={ruleColumn}
              permissionFieldName={field.name}
              disabled={disabled}
              ctdsFilterCallback={ctdsFilterCallback}
              {...getTestProps(testId, 'content-type-definition', 'testId')}
            />
          )}
        </form.AppField>

        <Button
          buttonSize="lg"
          onClick={onRemove}
          iconImage={<DeleteIcon className={'w-5 h-5 dark:text-slate-200'} />}
          buttonColor="borderless"
          iconPosition="top"
          disabled={disabled}
          additionalClasses={'p-3 pr-0 hover:opacity-50 justify-center w-fit'}
          type="button"
          {...getTestProps(testId, 'delete-rule', 'testId')}
        />
      </div>

      <div className="w-full grid grid-cols-4 sm:hidden justify-items-center dark:text-slate-300">
        {ruleColumn.map(({ name, label }) => (
          <span key={name} className="uppercase text-sm">
            {label}
          </span>
        ))}
      </div>

      <div className="w-full grid grid-cols-4 justify-items-center">
        {ruleColumn.map(({ name }) => (
          <GlobalRuleCheckbox
            key={name}
            ruleName={name}
            disabled={disabled}
            {...getTestProps(testId, `global-${name}`, 'testId')}
          />
        ))}
      </div>

      <form.AppField name={`${field.name}.contentTypeDefinition.id`}>
        {(subField) => (
          <subField.ErrorField additionalClasses="col-span-2 -mt-3" />
        )}
      </form.AppField>

      {type === 'CO' && (
        <form.Subscribe
          selector={({ values }) =>
            deepReadKeyValue(
              `${field.name}.contentTypeDefinition`,
              values,
            ) as PermissionContentTypeDefinition
          }
        >
          {(contentTypeDefinition) =>
            contentTypeDefinition?.metaDefinition ? (
              <form.AppField name={`${field.name}.attributesPermission`}>
                {(subField) => (
                  <subField.FieldPermissions
                    contentTypeDefinition={contentTypeDefinition}
                    ruleColumn={ruleColumn}
                    disabled={disabled}
                    {...getTestProps(testId, `fields`, 'testId')}
                  />
                )}
              </form.AppField>
            ) : null
          }
        </form.Subscribe>
      )}
    </>
  );
};

export default PermissionField;
